import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Loading } from '@carbon/react';
import { UnitBuilderPaneHeader } from './unit-builder-pane-header';
import { TypeCurveGunBarrel } from 'pages/type-curves/type-curve-gun-barrel';
import type {
  GunBarrelPositionEdit,
  GunBarrelSelectionInfo,
  GunBarrelChartHandle,
  GunBarrelEditAxis,
} from 'components/charts/gun-barrel-chart';
import { UnitBuilderRowResizer } from './unit-builder-row-resizer';
import { UnitBuilderSlotsTable } from './unit-builder-slots-table';
import { UnitBuilderSpacingControls } from './unit-builder-spacing-controls';
import { UnitBuilderEditRail } from './unit-builder-edit-rail';
import { LandingPendingBanner } from './landing-pending-banner';
import { useBuilderGeneration } from './use-builder-generation';
import { useBuilderSpacing, type BuilderSpacing } from './use-builder-spacing';
import {
  tcInventoryGunBarrelScopeAtom,
  tcUnitSpacingConfigsAtom,
  type InventorySpacingValues,
} from 'pages/type-curves/type-curve-filters/atoms';
import {
  slotTableStateAtoms,
  spacingEditsAtoms,
} from 'pages/type-curves/inventory-surface/atoms';
import { useUnitBuilder } from './context';
import {
  reslotRequestAtom,
  unitRegeneratingAtom,
  scopedSlotKeysAtom,
  ltdLateralOverridesAtom,
  selectedLtdSlotIdsAtom,
  valuationTargetAtom,
  builderSetbackAtom,
  builderSelectedFormationAtom,
} from './atoms';
import { useLtdLaterals } from './use-ltd-lateral';
import { useMergeGeometries } from './use-merge-geometries';
import {
  useCompositeActions,
  useUnitComposites,
} from './use-composite-wellbores';
import { MergeGeometriesPanel } from './merge-geometries-panel';
import {
  lateralLengthFt,
  lateralTrimsFt,
  resizeLateralToLength,
  type TrimFrom,
} from './ltd-lateral-trim';
import type { SlotGeometryEdit } from 'types/api-payloads';
import style from './unit-builder-modal.module.scss';

interface UnitBuilderGunBarrelProps {
  typeCurveId: number;
  /** Header detail the slots query keys on. */
  typeCurveHeaderDetailsId: number;
  structureGridCollectionId: number | null;
  horizontalWellSpacingFt: number;
  /** Formation of the scoped type curve, shown in the edit rail. */
  formationCode?: string | null;
  /** The scoped leaf's zone row, which scopes a generation run. */
  inventoryFormationPackageId?: number | null;
  /** Stored spacing for the scoped unit, seeding the controls. */
  storedSpacing?: BuilderSpacing;
  /** Step the scoped formation through the tree. */
  onStepFormation?: (delta: 1 | -1) => void;
  canStepFormationUp?: boolean;
  canStepFormationDown?: boolean;
  refetchVerifications?: () => void;
  onInventoryVerdictChanged?: () => void;
}

/**
 * The builder's right pane: spacing controls, the gun barrel with its edit rail,
 * and the slots table beneath — the same composition the mock's pane has.
 *
 * @param {UnitBuilderGunBarrelProps} props - Chart inputs, spacing seed and formation stepping.
 * @return {JSX.Element} The pane.
 */
export const UnitBuilderGunBarrel: FC<UnitBuilderGunBarrelProps> = ({
  typeCurveId,
  typeCurveHeaderDetailsId,
  structureGridCollectionId,
  horizontalWellSpacingFt,
  formationCode = null,
  inventoryFormationPackageId = null,
  storedSpacing,
  refetchVerifications,
  onInventoryVerdictChanged,
}) => {
  const { inventoryWorkspaceId, unitId, promoted } = useUnitBuilder();

  // The arrows step through the formations from the SPACING call — one per
  // configured zone (`tcUnitSpacingConfigsAtom`, filtered to the workspace's
  // visible packages) — not the type-curve tree, so a user can scope a formation
  // above or below the leaf's own. Each step is a concrete `(formation,
  // inventoryFormationPackageId)` pair that keys the effective spacing, the
  // readout and the chart highlight.
  const spacingConfigs = useAtomValue(tcUnitSpacingConfigsAtom);
  const gunBarrelScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  // Flips true on every slots (re)fetch — drives the pane's loading overlay.
  const slotsLoading =
    useAtomValue(slotTableStateAtoms.builder)?.loading ?? false;
  // A rotate's background regeneration is in flight: Generate LTD Inventory shows
  // busy and is held off until every formation has reported, so a manual run
  // can't collide with the one the server is already doing.
  const regenerating = useAtomValue(unitRegeneratingAtom);
  // The gun barrel's actually-drawn bands (published by the chart, shallow→deep).
  // The arrow set is intersected with these so a step always lands on a formation
  // with a visible band — a spacing zone with no band (e.g. "UNKNOWN", or a
  // package whose formation the chart doesn't draw) would otherwise highlight
  // nothing.
  const [chartFormations, setChartFormations] = useState<string[]>([]);
  const spacingFormations = useMemo(() => {
    const ids = gunBarrelScope?.inventoryFormationPackageIds;
    if (!spacingConfigs) return [];
    const bandNames = new Set(chartFormations.map((n) => n.toUpperCase()));
    // The chart draws its bands shallow→deep; index them so the stepper follows
    // that SAME visual order (↑ = shallower/up, ↓ = deeper/down), instead of the
    // unrelated order the spacing call returns.
    const bandOrder = new Map(
      chartFormations.map((n, i) => [n.toUpperCase(), i])
    );
    return (
      spacingConfigs
        .filter((c) => {
          const fmt = c.formation.toUpperCase();
          if (ids && !ids.includes(c.inventoryFormationPackageId)) return false;
          // "UNKNOWN" is never a real formation to scope: it has no band to
          // highlight. Its slots are editable under every highlight instead
          // (the chart's editable set), so an unlanded permit can still be moved.
          if (fmt === 'UNKNOWN') return false;
          // Once the chart's bands are known, keep only the common ones (a zone
          // with no drawn band can't be highlighted). Before they load, keep the
          // spacing set so the arrows aren't empty.
          if (bandNames.size > 0 && !bandNames.has(fmt)) return false;
          return true;
        })
        .map((c) => ({
          formation: c.formation,
          inventoryFormationPackageId: c.inventoryFormationPackageId,
        }))
        // Not-yet-drawn bands (chart still loading) sort last, preserving the
        // spacing order until the bands resolve.
        .sort(
          (a, b) =>
            (bandOrder.get(a.formation.toUpperCase()) ?? Infinity) -
            (bandOrder.get(b.formation.toUpperCase()) ?? Infinity)
        )
    );
  }, [spacingConfigs, gunBarrelScope, chartFormations]);

  // Selected zone, by package id — seeded from the leaf's own zone, reset when
  // the leaf changes. The arrows move through `spacingFormations`.
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(
    inventoryFormationPackageId
  );
  useEffect(
    () => setSelectedPackageId(inventoryFormationPackageId),
    [inventoryFormationPackageId]
  );

  const selectedIndex = spacingFormations.findIndex(
    (f) => f.inventoryFormationPackageId === selectedPackageId
  );
  // Before the spacing call lands (or if the leaf's zone isn't in the set), fall
  // back to the first zone for the highlight; the arrows stay disabled until the
  // selection is resolvable within the set.
  const effectiveIndex =
    selectedIndex >= 0 ? selectedIndex : spacingFormations.length ? 0 : -1;
  const selectedEntry =
    effectiveIndex >= 0 ? spacingFormations[effectiveIndex] : null;
  // The formation NAME drives the chart highlight + slot editability; the package
  // id keys the effective spacing below. Both fall back to the leaf before the
  // spacing set resolves.
  const selectedFormation = selectedEntry?.formation ?? formationCode;
  const selectedPkgId =
    selectedEntry?.inventoryFormationPackageId ??
    inventoryFormationPackageId ??
    null;

  const stepFormation = useCallback(
    (delta: 1 | -1) =>
      setSelectedPackageId((cur) => {
        const idx = spacingFormations.findIndex(
          (f) => f.inventoryFormationPackageId === cur
        );
        const base = idx >= 0 ? idx : 0;
        const next = spacingFormations[base + delta];
        return next ? next.inventoryFormationPackageId : cur;
      }),
    [spacingFormations]
  );
  const canStepFormationUp = effectiveIndex > 0;
  const canStepFormationDown =
    effectiveIndex >= 0 && effectiveIndex < spacingFormations.length - 1;

  // Publish the stepped band + zone to the builder MAP (a separate pane), which
  // scopes its own decoupled `inventory_unit_slots` fetch to it — never the main
  // page's `tcHighlightedFormationAtom`. Set on change; cleared only on unmount.
  const setBuilderFormation = useSetAtom(builderSelectedFormationAtom);
  useEffect(() => {
    setBuilderFormation({
      formation: selectedFormation,
      inventoryFormationPackageId: selectedPkgId,
    });
  }, [selectedFormation, selectedPkgId, setBuilderFormation]);
  useEffect(() => () => setBuilderFormation(null), [setBuilderFormation]);

  // Lifted from the chart, which is the only thing that fetches it.
  const [unitExtent, setUnitExtent] = useState<{
    leftXFt: number;
    rightXFt: number;
  } | null>(null);
  // Pending drags, keyed `slot:{id}` / `uwi:{uwi}` — the set Update Geometries
  // re-cuts.
  const [positionEdits, setPositionEdits] = useState<
    ReadonlyMap<string, GunBarrelPositionEdit>
  >(new Map());
  const [showSlotLabels, setShowSlotLabels] = useState(false);
  const [scopedSlotKeys, setScopedSlotKeys] = useAtom(scopedSlotKeysAtom);

  const merge = useMergeGeometries(selectedFormation, true);
  const { composites: unitComposites } = useUnitComposites();
  const { split: splitComposite } = useCompositeActions();
  const [splittingId, setSplittingId] = useState<number | null>(null);
  useEffect(() => {
    if (splittingId === null) return;
    if (!unitComposites.some((c) => c.id === splittingId)) {
      setSplittingId(null);
    }
  }, [unitComposites, splittingId]);
  const ltdLabelsRef = useRef<ReadonlySet<string>>(new Set());
  ltdLabelsRef.current = useMemo(
    () =>
      new Set(
        [...merge.slotsById.values()]
          .filter((slot) => !slot.isPermit)
          .map((slot) => `slot:${slot.label}`)
      ),
    [merge.slotsById]
  );

  // Changing the formation band (arrows) drops everything but LTDs from the
  // selection: those stay picked for Merge Geometries (the chart no longer lets
  // them be moved), while wells and permits of the old band can't carry over.
  useEffect(() => {
    setScopedSlotKeys((prev) => {
      const next = new Set(
        [...prev].filter((k) => ltdLabelsRef.current.has(k))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [selectedPackageId, setScopedSlotKeys]);

  // Live position + count of the chart selection, published by the chart, so the
  // edit rail can show the selected property's X/TVD and gate its nudge controls.
  const [selectionInfo, setSelectionInfo] = useState<GunBarrelSelectionInfo>({
    count: 0,
    single: null,
    ltdSlotIds: [],
  });
  // The chart's imperative handle — the rail nudges / sets / resets through it.
  const chartHandleRef = useRef<GunBarrelChartHandle>(null);
  const handleNudge = useCallback(
    (axis: GunBarrelEditAxis, deltaFt: number) =>
      chartHandleRef.current?.nudgeSelected(axis, deltaFt),
    []
  );
  const handleSetAbsolute = useCallback(
    (axis: GunBarrelEditAxis, valueFt: number) =>
      chartHandleRef.current?.setSelectedAbsolute(axis, valueFt),
    []
  );

  // ── Lateral-length editing (one or several scoped LTDs) ────────────────────
  // Which end a length edit is taken off; the map grips and this switch agree.
  const [trimFrom, setTrimFrom] = useState<TrimFrom>('high');
  // Every selected editable LTD id, published so the map pane (a separate
  // subtree) grips the same laterals. Stable string key avoids churn.
  const ltdSlotIds = selectionInfo.ltdSlotIds;
  const ltdSlotIdsKey = ltdSlotIds.join(',');
  const setSelectedLtdSlotIds = useSetAtom(selectedLtdSlotIdsAtom);
  useEffect(() => {
    setSelectedLtdSlotIds(new Set(ltdSlotIds));
    // ltdSlotIdsKey stands in for the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ltdSlotIdsKey, setSelectedLtdSlotIds]);

  const { laterals, setManyCoords } = useLtdLaterals(ltdSlotIds);
  // Trims and any open valuation Notes are front-end only and unit-scoped:
  // discard them when the unit changes.
  const ltdOverrides = useAtomValue(ltdLateralOverridesAtom);
  const setLtdOverrides = useSetAtom(ltdLateralOverridesAtom);
  const [valuationTarget, setValuationTarget] = useAtom(valuationTargetAtom);
  useEffect(() => {
    setLtdOverrides(new Map());
    setValuationTarget(null);
  }, [unitId, setLtdOverrides, setValuationTarget]);

  // The right-click that opens the Notes section also reveals the type-curve
  // tooltip, and that one only clears on a left-click ON the chart — which
  // saving from the edit rail never is. Dismiss it when the target closes.
  const hadValuationTarget = useRef(false);
  useEffect(() => {
    if (valuationTarget) {
      hadValuationTarget.current = true;
    } else if (hadValuationTarget.current) {
      hadValuationTarget.current = false;
      chartHandleRef.current?.hideTypeCurveTooltip();
    }
  }, [valuationTarget]);

  // Reset clears every pending property edit: the chart's position moves AND the
  // front-end lateral-length trims. The trims live in the shared overrides atom,
  // so resetEdits() alone would leave them behind (Reset stays disabled and the
  // trimmed laterals persist on the map grips).
  const handleResetEdits = useCallback(() => {
    chartHandleRef.current?.resetEdits();
    setLtdOverrides(new Map());
  }, [setLtdOverrides]);

  // Only VALUED laterals whose geometry has loaded can be trimmed.
  const editableLaterals = laterals.filter(
    (l) => l.valued !== false && l.fullCoords && l.currentCoords
  );
  const lengthEnabled = editableLaterals.length > 0;
  // The shared length, shown in the field: the common rounded length when every
  // scoped lateral agrees, else null so the field reads "multiple" (the mock).
  const roundedLengths = editableLaterals.map((l) =>
    Math.round(lateralLengthFt(l.currentCoords as GeoJSON.Position[]))
  );
  const lengthFt =
    roundedLengths.length > 0 &&
    roundedLengths.every((v) => v === roundedLengths[0])
      ? roundedLengths[0]
      : null;
  const fullRounded = editableLaterals.map((l) =>
    Math.round(lateralLengthFt(l.fullCoords as GeoJSON.Position[]))
  );
  const fullLengthFt =
    fullRounded.length > 0 && fullRounded.every((v) => v === fullRounded[0])
      ? fullRounded[0]
      : null;

  // Set an exact length on every scoped lateral (converges a mixed selection).
  // The Trim-from end moves; the other end holds its CURRENT position (no snap to
  // the unit edge).
  const applyLength = useCallback(
    (targetFt: number) => {
      setManyCoords(
        editableLaterals.map((l) => [
          l.slotId,
          resizeLateralToLength(
            l.maxCoords as GeoJSON.Position[],
            l.currentCoords as GeoJSON.Position[],
            targetFt,
            trimFrom
          ),
        ])
      );
    },
    [editableLaterals, setManyCoords, trimFrom]
  );
  // Nudge is relative per lateral, so an unequal selection keeps its differences.
  const handleNudgeLength = useCallback(
    (deltaFt: number) => {
      setManyCoords(
        editableLaterals.map((l) => [
          l.slotId,
          resizeLateralToLength(
            l.maxCoords as GeoJSON.Position[],
            l.currentCoords as GeoJSON.Position[],
            lateralLengthFt(l.currentCoords as GeoJSON.Position[]) + deltaFt,
            trimFrom
          ),
        ])
      );
    },
    [editableLaterals, setManyCoords, trimFrom]
  );

  // The builder's OWN live spacing edits (per zone). Scoped to the builder
  // surface, so an edit updates the builder's readouts + per-band setback/drainage
  // immediately WITHOUT reaching into the main page (`spacingEditsAtoms.main`).
  const spacingEdits = useAtomValue(spacingEditsAtoms.builder);
  const setSpacingEdits = useSetAtom(spacingEditsAtoms.builder);

  // The selected zone's config from the spacing_configs call — the API baseline
  // the inputs seed from and edits are diffed against.
  const configSeed = useMemo<BuilderSpacing | null>(() => {
    if (selectedPkgId === null || !spacingConfigs) return null;
    const cfg = spacingConfigs.find(
      (c) => c.inventoryFormationPackageId === selectedPkgId
    );
    if (!cfg) return null;
    return {
      wellSpacingFt: cfg.wellSpacingFt,
      crossLineSetbackFt: cfg.crossLineSetbackFt,
      inLineSetbackFt: cfg.inLineSetbackFt,
      spacingToleranceFt: cfg.spacingToleranceFt,
    };
  }, [spacingConfigs, selectedPkgId]);

  // Effective seed for the inputs: a live edit for this zone wins over the config,
  // which wins over the stored/default seed. Stepping the formation re-seeds the
  // inputs — and through them the map ring + generation — to that zone's values.
  const effectiveSeed = useMemo<BuilderSpacing>(() => {
    const edit =
      selectedPkgId !== null ? spacingEdits[selectedPkgId] : undefined;
    return (
      edit ??
      configSeed ??
      storedSpacing ?? {
        wellSpacingFt: horizontalWellSpacingFt,
        crossLineSetbackFt: null,
        inLineSetbackFt: null,
        spacingToleranceFt: null,
      }
    );
  }, [
    selectedPkgId,
    spacingEdits,
    configSeed,
    storedSpacing,
    horizontalWellSpacingFt,
  ]);

  const spacing = useBuilderSpacing(effectiveSeed);

  // Publish an edit to the shared atom ONLY when the user actually changes a
  // value — imperatively, from the input's onChange. It must NOT be mirrored in
  // an effect: `spacing` is derived from this same atom (via `effectiveSeed`), and
  // `useBuilderSpacing` lags a render, so an effect writing `spacing` back would
  // oscillate forever (config ⇄ spurious edit → the spacing input blinks). An edit
  // that matches the config clears the entry (sparse map: no key = "no edit"), so
  // the main page falls back to the stored config.
  const writeSpacing = useCallback(
    (patch: Partial<BuilderSpacing>) => {
      spacing.set(patch);
      if (selectedPkgId === null) return;
      const merged: InventorySpacingValues = {
        wellSpacingFt: spacing.wellSpacingFt,
        crossLineSetbackFt: spacing.crossLineSetbackFt,
        inLineSetbackFt: spacing.inLineSetbackFt,
        spacingToleranceFt: spacing.spacingToleranceFt,
        ...patch,
      };
      const c = configSeed;
      const matchesConfig =
        c !== null &&
        c.wellSpacingFt === merged.wellSpacingFt &&
        c.crossLineSetbackFt === merged.crossLineSetbackFt &&
        c.inLineSetbackFt === merged.inLineSetbackFt &&
        c.spacingToleranceFt === merged.spacingToleranceFt;
      setSpacingEdits((prev) => {
        if (matchesConfig) {
          if (prev[selectedPkgId] === undefined) return prev;
          const next = { ...prev };
          delete next[selectedPkgId];
          return next;
        }
        return { ...prev, [selectedPkgId]: merged };
      });
    },
    [spacing, selectedPkgId, configSeed, setSpacingEdits]
  );

  // The spacing state handed to the controls, with `set` routed through the
  // shared-edit writer so a change also lands on the main page's readouts.
  const spacingForControls = useMemo(
    () => ({ ...spacing, set: writeSpacing }),
    [spacing, writeSpacing]
  );

  // Share the live setbacks with the map pane (it draws the keep-out ring). The
  // gun barrel's own cross-line band is driven by the prop below (same pane).
  const setBuilderSetback = useSetAtom(builderSetbackAtom);
  useEffect(() => {
    setBuilderSetback({
      crossLineSetbackFt: spacing.crossLineSetbackFt,
      inLineSetbackFt: spacing.inLineSetbackFt,
    });
  }, [spacing.crossLineSetbackFt, spacing.inLineSetbackFt, setBuilderSetback]);

  const generation = useBuilderGeneration(inventoryWorkspaceId, () => {
    // A finished run replaces the slots the pending edits referred to — clear
    // both the position edits and the lateral-length trim overlay.
    setPositionEdits(new Map());
    setLtdOverrides(new Map());
    refetchVerifications?.();
  });

  // The landing edits the drawn slots are still waiting on (Story 1). Published
  // by the chart from the slots it fetched, so a page opened mid-landing shows
  // the banner too — and follows those edits on the workspace channel, the way a
  // batch started here is followed, so their resolution refreshes this page.
  const [pendingLandingEditIds, setPendingLandingEditIds] = useState<number[]>(
    []
  );
  const pendingLandingKey = pendingLandingEditIds.join(',');
  const { watchLandings } = generation;
  useEffect(() => {
    if (pendingLandingEditIds.length > 0) watchLandings(pendingLandingEditIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingLandingKey stands in for the array identity
  }, [pendingLandingKey, watchLandings]);

  const movedSlotIds = useMemo(
    () =>
      [...positionEdits.values()]
        .map((edit) => edit.slotId)
        .filter((id): id is number => id !== null),
    [positionEdits]
  );
  // Update Geometries re-cuts any edited slot — a position move OR a lateral
  // trim (the mock enables it on both). The trim overlay is keyed by the same
  // numeric slot id `positionEdits` carry.
  const pendingSlotIds = useMemo(
    () => [...new Set([...movedSlotIds, ...ltdOverrides.keys()])],
    [movedSlotIds, ltdOverrides]
  );

  // The server geometry of every trimmed lateral (not just the selected ones),
  // so Update Geometries can derive each slot's trim amounts from the full line.
  const overrideSlotIds = useMemo(
    () => [...ltdOverrides.keys()],
    [ltdOverrides]
  );
  const { laterals: overrideLaterals } = useLtdLaterals(overrideSlotIds);

  const handleGenerate = useCallback(() => {
    if (unitId === null) return;
    generation.generateInventory({
      unitId,
      // Target the zone the arrows are on (its spacing is what's shown/edited),
      // falling back to the leaf's own zone before the spacing set resolves.
      inventoryFormationPackageId: selectedPkgId ?? inventoryFormationPackageId,
      spacing: {
        wellSpacingFt: spacing.wellSpacingFt,
        crossLineSetbackFt: spacing.crossLineSetbackFt,
        inLineSetbackFt: spacing.inLineSetbackFt,
        spacingToleranceFt: spacing.spacingToleranceFt,
      },
      ignoreExistingDevelopment: spacing.ignoreExistingDevelopment,
    });
  }, [unitId, selectedPkgId, inventoryFormationPackageId, generation, spacing]);

  // A rotate from the map toolbar asks for a re-slot; run the same generation
  // Generate LTD Inventory does. Skipped on mount so opening the builder does
  // not fire a run of its own.
  const reslotRequest = useAtomValue(reslotRequestAtom);
  const seenReslotRef = useRef(reslotRequest);
  useEffect(() => {
    if (reslotRequest === seenReslotRef.current) return;
    seenReslotRef.current = reslotRequest;
    handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reslotRequest]);

  const handleUpdateGeometries = useCallback(() => {
    if (unitId === null) return;

    // One ops row per edited slot, merging its position move and lateral trim.
    const byId = new Map<number, SlotGeometryEdit>();
    const opsFor = (slotId: number): SlotGeometryEdit => {
      const existing = byId.get(slotId);
      if (existing) return existing;
      const created: SlotGeometryEdit = { slotId };
      byId.set(slotId, created);
      return created;
    };

    // Position moves (LTD slots only; standalone wells carry no slotId).
    for (const edit of positionEdits.values()) {
      if (edit.slotId === null) continue;
      const ops = opsFor(edit.slotId);
      if (edit.dxFt !== 0) ops.dxFt = edit.dxFt;
      // FE `dTvdFt` is "+ = deeper"; the contract's `dtvdFt` is "negative
      // deepens", so the sign flips.
      if (edit.dTvdFt !== 0) ops.dtvdFt = -edit.dTvdFt;
    }

    // Lateral trims — how far each end moved in from the current server line
    // (negative = moved out). An extension rounds toward zero, never past the
    // limit it was held to.
    const roundTrim = (ft: number) => {
      return ft >= 0 ? Math.round(ft) : Math.ceil(ft);
    };
    for (const lateral of overrideLaterals) {
      const trimmed = ltdOverrides.get(lateral.slotId);
      if (!trimmed || !lateral.fullCoords) continue;
      const { trimStartFt, trimEndFt } = lateralTrimsFt(
        lateral.fullCoords,
        trimmed
      );
      const ops = opsFor(lateral.slotId);
      if (Math.abs(trimStartFt) >= 1) ops.trimStartFt = roundTrim(trimStartFt);
      if (Math.abs(trimEndFt) >= 1) ops.trimEndFt = roundTrim(trimEndFt);
    }

    // Drop rows that resolved to no actual change (only a `slotId`).
    const edits = [...byId.values()].filter(
      (row) => Object.keys(row).length > 1
    );
    if (edits.length === 0) return;
    generation.updateGeometries({ unitId, edits });
  }, [unitId, positionEdits, overrideLaterals, ltdOverrides, generation]);

  // Stable reference so the slots table's query doesn't re-fire on every render
  // (e.g. each selection change), which an inline array literal would cause.
  const slotCollectionIds = useMemo(() => {
    return structureGridCollectionId !== null
      ? [structureGridCollectionId]
      : undefined;
  }, [structureGridCollectionId]);

  return (
    <div className={style.gunBarrelStack}>
      {slotsLoading && (
        <div className={style.slotsLoadingOverlay} role="status">
          <Loading small withOverlay={false} description="Loading slots…" />
        </div>
      )}
      <div className={`${style.pane} ${style.paneFixed}`}>
        <UnitBuilderPaneHeader
          title="Gun Barrel"
          info="A cross-section looking down the unit azimuth. Squares are LTD properties, circles existing ones; the shaded vertical bands are the Cross-line Setback, which properties can't enter. Unit Well Spacing sets the grid, Spacing Tolerance lets it repack to fit more drainage sectors, and In-line Setback trims each lateral at both ends. Click a property inside the unit (or tick its row in the table) to scope it — only then can it be moved or trimmed. Drag a scoped property to reposition it, or use Edit Property Geometries to type exact values. Generate LTD Inventory re-lays the whole unit; Update Geometries re-cuts only the properties you moved."
        />
        <UnitBuilderSpacingControls spacing={spacingForControls} />

        <LandingPendingBanner count={pendingLandingEditIds.length} />

        <div
          className={style.gunBarrelBody}
          data-resize-target="--unit-builder-chart-h"
        >
          <div className={style.gunBarrelChart}>
            <TypeCurveGunBarrel
              chartOnly
              hideUnitNavigator
              // Follows the row resizer: the property is derived in the SCSS from
              // the one the handle writes, so the canvas re-resolves in CSS
              // rather than re-rendering the chart on every drag frame.
              chartHeight="var(--unit-builder-chart-canvas-h)"
              chartHandleRef={chartHandleRef}
              onSelectionInfoChange={setSelectionInfo}
              highlightedFormationOverride={selectedFormation}
              onFormationsChange={setChartFormations}
              onPendingLandingsChange={setPendingLandingEditIds}
              onUnitBoundariesChange={setUnitExtent}
              onPositionEditsChange={setPositionEdits}
              selectedSlotKeys={scopedSlotKeys}
              onSelectedSlotKeysChange={setScopedSlotKeys}
              crossFormationPicking={unitId !== null}
              showSlotLabels={showSlotLabels}
              typeCurveId={typeCurveId}
              structureGridCollectionId={structureGridCollectionId}
              horizontalWellSpacingFt={horizontalWellSpacingFt}
              crossLineSetbackFt={spacing.crossLineSetbackFt}
              wellSpacingFt={spacing.wellSpacingFt}
              refetchVerifications={refetchVerifications}
              onInventoryVerdictChanged={onInventoryVerdictChanged}
            />
          </div>

          <UnitBuilderEditRail
            formationCode={selectedFormation}
            onStepFormation={stepFormation}
            canStepUp={canStepFormationUp}
            canStepDown={canStepFormationDown}
            showSlotLabels={showSlotLabels}
            onShowSlotLabelsChange={setShowSlotLabels}
            onGenerateInventory={unitId !== null ? handleGenerate : undefined}
            generating={generation.generating || regenerating}
            onUpdateGeometries={handleUpdateGeometries}
            updating={generation.updating}
            pendingGeometryCount={pendingSlotIds.length}
            scopedSlotCount={scopedSlotKeys.size}
            selectionInfo={selectionInfo}
            onNudge={handleNudge}
            onSetAbsolute={handleSetAbsolute}
            onResetEdits={handleResetEdits}
            lengthEnabled={lengthEnabled}
            lengthFt={lengthFt}
            fullLengthFt={fullLengthFt}
            trimFrom={trimFrom}
            onTrimFromChange={setTrimFrom}
            onNudgeLength={handleNudgeLength}
            onSetLength={applyLength}
            empty={unitExtent === null}
            merge={
              unitId === null
                ? undefined
                : {
                    onToggle: merge.draft ? merge.closeMerge : merge.openMerge,
                    active: merge.draft !== null,
                    disabledReason: promoted
                      ? 'This workspace has been promoted; composites are frozen'
                      : merge.disabledReason,
                    selectedCount: merge.selectedCount,
                    otherFormations: merge.otherFormations,
                    otherFormationCount: merge.otherFormationCount,
                    panel: merge.draft && (
                      <MergeGeometriesPanel
                        draft={merge.draft}
                        landingOptions={merge.landingOptions}
                        bottomHoleOptions={merge.bottomHoleOptions}
                        landingKey={merge.landingKey}
                        bottomHoleKey={merge.bottomHoleKey}
                        chain={merge.chain}
                        creating={merge.creating}
                        onAssign={merge.assignEnd}
                        onCancel={merge.closeMerge}
                        onCreate={merge.createComposite}
                      />
                    ),
                    composites: unitComposites.flatMap((c) => {
                      if (c.id === null) return [];
                      return [
                        {
                          id: c.id,
                          name: c.name,
                          memberCount: c.legCount,
                          producingLengthFt: c.producingLengthFt,
                        },
                      ];
                    }),
                    onSplit: promoted
                      ? undefined
                      : (id) => {
                          const c = unitComposites.find((x) => x.id === id);
                          if (c && c.id !== null) {
                            setSplittingId(c.id);
                            splitComposite({ id: c.id, name: c.name }, (ok) => {
                              if (!ok) setSplittingId(null);
                            });
                          }
                        },
                    splittingId,
                  }
            }
          />
        </div>
      </div>

      <UnitBuilderRowResizer
        cssVar="--unit-builder-chart-h"
        min={200}
        label="Resize chart"
      />

      <UnitBuilderSlotsTable
        typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
        collectionIds={slotCollectionIds}
        unitExtent={unitExtent}
      />
    </div>
  );
};
